import { createSlice } from "@reduxjs/toolkit"
import isEqual from "lodash.isequal"
import { persistReducer } from "redux-persist"
import storage from "redux-persist/lib/storage"

import { frozenStateReconciler } from "../../../Utils/persistHelpers"
import { defaultFiltersByFolders } from "../../../Utils/utils"
import { logout } from "../../LoginPage/redux/auth.actions"
import { preferencesSuccess } from "../../User/redux/user.actions"
import { CREDITS_SLICE_NAME } from "./credits.constants"
import {
  attachProofCredit,
  deleteCredit,
  getCreditDetails,
  getCredits,
  getCreditsExport,
  getCreditsFilterOptions,
  getCreditsStatistics,
  pinCredit,
  resubmitCreditRequest,
  updateCredit
} from "./credits.thunks"

/* ------------- Initial State ------------- */

const columns = [
  //{field_id: 'request_status', label: 'Request status', active: true},
  { field_id: "transaction_id", label: "Transaction ID", active: true },
  { field_id: "alert_status", label: "Alert status", active: true },
  { field_id: "request_reason", label: "Request reason", active: true },
  { field_id: "request_date_timestamp", label: "Request date", active: true },
  { field_id: "alert_service", label: "Alert source", active: true },
  { field_id: "request_amount", label: "Alert cost", active: true },
  { field_id: "alert_id", label: "Alert ID", active: true },
  { field_id: "merchant_descriptor", label: "Billing descriptor", active: true },
  { field_id: "card_last4", label: "Card", active: true },
  { field_id: "amount", label: "Order value", active: true },
  { field_id: "transaction_timestamp", label: "Transaction date", active: true },
  { field_id: "request_type", label: "Request type", active: true },

  { field_id: "alert_reason", label: "Alert reason", active: false },
  { field_id: "alert_timestamp", label: "Alert date", active: false },
  { field_id: "country", label: "Country", active: false },
  { field_id: "bank_name", label: "Bank", active: false },
  { field_id: "payment_processor", label: "Processor", active: false },
  { field_id: "arn", label: "ARN", active: false },
  { field_id: "auth_code", label: "Auth.Code", active: false },
  { field_id: "alert_type", label: "Type", active: false },
  { field_id: "rejection_reason", label: "Rejection reason", active: false },
  { field_id: "original_alert_id", label: "Original alert", active: false }
]

const columnsAdmin = [
  //{field_id: 'request_type', label: 'Request type', active: true},
  { field_id: "request_status", label: "Request status", active: true },
  { field_id: "provider_status", label: "Provider status", active: true },
  { field_id: "credits_automation", label: "Credits Automation", active: true },
  { field_id: "request_date_timestamp", label: "Request date", active: true },
  { field_id: "update_date_timestamp", label: "Update date", active: true },
  { field_id: "request_amount", label: "Alert cost", active: true },
  { field_id: "business_account", label: "Business account", active: true },
  { field_id: "user_email", label: "User email", active: true },
  { field_id: "alert_status", label: "Alert status", active: true },
  { field_id: "alert_timestamp", label: "Alert date", active: true },
  { field_id: "alert_service", label: "Alert source", active: true },
  { field_id: "request_reason", label: "Request reason", active: true },
  { field_id: "alert_id", label: "Alert ID", active: true },
  { field_id: "transaction_id", label: "Transaction ID", active: true },
  { field_id: "rejection_reason", label: "Rejection reason", active: true },
  { field_id: "provider_reason", label: "Provider reason", active: true },
  { field_id: "attachments", label: "Attachments", active: true },

  { field_id: "admin_comment", label: "Comment", active: true },
  { field_id: "admin_screenshots", label: "Screenshots", active: true },
  { field_id: "original_alert_id", label: "Original alert", active: true }
]

const initialState = {
  fetchingStatistics: false,
  statistics: {
    //used for header with navIcon (common statistic)
    all: 0,
    ethoca: 0,
    cdrn: 0,
    rdr: 0,
    verifi: 0,
    manual: 0,
    system: 0,
    requires_proof: 0
  },
  fetching: false,
  result: {
    count: 0,
    next: null,
    previous: null,
    results: [],
    requires_proof_list: [],
    statistic: {
      all: 0,
      ethoca: 0,
      cdrn: 0,
      rdr: 0,
      verifi: 0,
      manual: 0,
      system: 0,
      requires_proof: 0
    }
  },
  defaultColumns: [{ field_id: "request_status", label: "Request status" }, ...columns],
  columns: [...columns],

  defaultColumnsAdmin: [{ field_id: "request_type", label: "Request type" }, ...columnsAdmin],
  columnsAdmin: [...columnsAdmin],

  filtersLastUpd: null,
  filters: {
    alert_timestamp: null,
    transaction_timestamp: null,
    request_date_timestamp: null,
    update_date_timestamp: null,

    alert_status: "all",
    alert_type: "all",
    alert_reason: "all",
    alert_service: "all",
    request_status: "all",
    request_type: "all",
    request_reason: "all",
    rejection_reason: "all",
    provider_reason: "all",
    provider_status: "all",
    credits_automation: "all",

    user_email: [],
    customer_email: [],
    business_account: [],
    business_url: [],

    request_amount: { min: null, max: null, type: null },
    amount: { min: null, max: null, type: null },
    attachments: { min: null, max: null, type: null },

    bank_name: "all",
    merchant_descriptor: "all",
    country: "all",
    payment_processor: "all",
    alert_id: [],
    transaction_id: [],
    arn: [],
    auth_code: [],
    card_last4: [],
    exclude_fields: [],
    is_archived: null,
    admin_comment: [],
    admin_screenshots: "all"
  },

  fetchingFilterOptions: false,
  filterOptions: {
    alert_status: [],
    alert_type: [],
    alert_reason: [],
    country: [],
    bank_name: [],
    payment_processor: [],
    customer_unsubscribed: [],
    admin_comment: []
  },

  visibleFilters: { request_status: true, transaction_id: true, alert_status: true },
  visibleFiltersAdmin: { request_status: true, provider_status: true, business_account: true },

  exportFetching: false,
  exportResult: null,

  saveFetching: false,
  saveResult: null,

  creditDetailsFetching: false,
  creditDetails: null,

  creditsViewMode: "table"
}

const slice = createSlice({
  name: CREDITS_SLICE_NAME,
  initialState,
  reducers: {
    // Legacy call sites dispatched this with two arguments — `creditsSetProp(key, value)`.
    // RTK actions carry a single payload, so callers pass `creditsSetProp({ key, value })`,
    // matching the `alertsSetProp`/`requestsAutoRefundSetProp` slices.
    creditsSetProp(state, action) {
      const { key, value } = action.payload
      if (["filters", "visibleFilters", "visibleFiltersAdmin"].includes(key)) {
        if (key === "filters") state.filtersLastUpd = new Date().getTime()
        state[key] = { ...state[key], ...value }
      } else {
        state[key] = value
      }
    },
    clearCreditsFilters(state, action) {
      state.filters = { ...initialState.filters, ...action.payload }
    },
    resetSaveResult(state) {
      state.saveResult = null
    },
    creditDetailsReset(state) {
      state.creditDetails = null
    }
  },
  extraReducers: (builder) => {
    builder
      /* ---------------- statistics ---------------- */
      .addCase(getCreditsStatistics.pending, (state) => {
        state.fetchingStatistics = true
      })
      .addCase(getCreditsStatistics.fulfilled, (state, action) => {
        const statistics = action.payload
        state.fetchingStatistics = false
        state.statistics = {
          ...statistics,
          verifi: (statistics?.cdrn || 0) + (statistics?.rdr || 0)
        }
      })
      .addCase(getCreditsStatistics.rejected, (state) => {
        state.fetchingStatistics = false
      })

      /* ---------------- filter options ---------------- */
      .addCase(getCreditsFilterOptions.pending, (state) => {
        state.fetchingFilterOptions = true
      })
      .addCase(getCreditsFilterOptions.fulfilled, (state, action) => {
        state.fetchingFilterOptions = false
        state.filterOptions = action.payload
      })
      .addCase(getCreditsFilterOptions.rejected, (state) => {
        state.fetchingFilterOptions = false
      })

      /* ---------------- list ---------------- */
      .addCase(getCredits.pending, (state) => {
        state.fetching = true
      })
      .addCase(getCredits.fulfilled, (state, action) => {
        state.fetching = false
        state.result = action.payload
      })
      .addCase(getCredits.rejected, (state) => {
        state.fetching = false
      })

      /* ---------------- update ---------------- */
      .addCase(updateCredit.pending, (state) => {
        state.fetching = true
      })
      .addCase(updateCredit.fulfilled, (state, action) => {
        const result = action.payload
        state.fetching = false
        state.saveResult = result?.message
          ? { type: "credit_update", message: result?.message }
          : "credit_update"
      })
      .addCase(updateCredit.rejected, (state) => {
        state.fetching = false
      })

      /* ---------------- pin ---------------- */
      .addCase(pinCredit.pending, (state) => {
        state.fetching = true
      })
      .addCase(pinCredit.fulfilled, (state) => {
        state.fetching = false
        state.saveResult = "pin"
      })
      .addCase(pinCredit.rejected, (state) => {
        state.fetching = false
      })

      /* ---------------- delete ---------------- */
      .addCase(deleteCredit.pending, (state) => {
        state.fetching = true
      })
      .addCase(deleteCredit.fulfilled, (state) => {
        state.fetching = false
        state.saveResult = "delete"
      })
      .addCase(deleteCredit.rejected, (state) => {
        state.fetching = false
      })

      /* ---------------- export ---------------- */
      .addCase(getCreditsExport.pending, (state) => {
        state.exportFetching = true
      })
      .addCase(getCreditsExport.fulfilled, (state, action) => {
        state.exportFetching = false
        state.exportResult = action.payload
      })
      .addCase(getCreditsExport.rejected, (state) => {
        state.exportFetching = false
      })

      /* ---------------- details ---------------- */
      .addCase(getCreditDetails.pending, (state) => {
        state.creditDetailsFetching = true
      })
      .addCase(getCreditDetails.fulfilled, (state, action) => {
        state.creditDetailsFetching = false
        state.creditDetails = action.payload
      })
      .addCase(getCreditDetails.rejected, (state) => {
        state.creditDetailsFetching = false
      })

      /* ---------------- attach proof ---------------- */
      .addCase(attachProofCredit.pending, (state) => {
        state.saveFetching = true
      })
      .addCase(attachProofCredit.fulfilled, (state) => {
        state.saveFetching = false
        state.saveResult = "attach_proof"
      })
      .addCase(attachProofCredit.rejected, (state) => {
        state.saveFetching = false
      })

      /* ---------------- resubmit ---------------- */
      .addCase(resubmitCreditRequest.pending, (state) => {
        state.saveFetching = true
        state.fetching = true
      })
      .addCase(resubmitCreditRequest.fulfilled, (state) => {
        state.saveFetching = false
        state.saveResult = "credit_request_resubmit"
        state.fetching = false
      })
      // The legacy saga routed this request's failure through the shared `creditsFailure`,
      // which only cleared `fetching` — leaving its own `RESUBMIT_CREDIT_REQUEST_FAILURE`
      // handler unreachable and `saveFetching` stuck on. With one rejected case per thunk,
      // that dedicated handler is what runs now, so the save spinner clears too.
      .addCase(resubmitCreditRequest.rejected, (state) => {
        state.saveFetching = false
        state.fetching = false
      })

      /* ---------------- logout ---------------- */
      .addCase(logout, () => initialState)

      /* ---------------- restore saved preferences ---------------- */
      .addCase(preferencesSuccess, (state, { payload }) => {
        const preference = payload?.credits?.data
        if (!preference || preference.version !== CREDITS_PREFERENCES_VERSION) return
        if (
          !preference.filters ||
          !preference.columns ||
          !preference.columnsAdmin ||
          !preference.visibleFilters ||
          !preference.visibleFiltersAdmin
        )
          return

        state.filters = preference.filters
        state.columns = preference.columns
        state.columnsAdmin = preference.columnsAdmin
        state.visibleFilters = preference.visibleFilters
        state.visibleFiltersAdmin = preference.visibleFiltersAdmin
      })
  }
})

export const creditsActions = slice.actions

// Bump when the shape of the saved `{ version, filters, columns, columnsAdmin, visibleFilters,
// visibleFiltersAdmin }` payload changes.
export const CREDITS_PREFERENCES_VERSION = 1

/* ------------- Selectors ------------- */

export const selectAreFiltersDefault = (state, folder) => {
  const defaultFilters = defaultFiltersByFolders.credits?.[folder] || initialState.filters

  const filtersToCompare = {
    ...initialState.filters,
    provider_status: defaultFilters.provider_status,
    is_archived: defaultFilters.is_archived
  }

  return isEqual(filtersToCompare, state.credits.filters)
}

// Starts at 5 because the legacy reducer persisted this same `credits` key up to version 4 —
// the bump is what makes `migrate` discard that old shape instead of feeding it to this slice.
// Now 6: Staging also moved the legacy reducer to 5 when it added the `credits_automation`
// column/filter, so a persisted version of 5 is ambiguous — 6 discards both stale shapes.
const PERSISTED_REDUCER_VERSION = 6

export const persistedReducer = persistReducer(
  {
    key: CREDITS_SLICE_NAME,
    storage,
    // Only the user's own table setup survives a reload — the credits list, statistics, filter
    // options, details and export/save results are all re-fetched or transient.
    whitelist: [
      "filters",
      "filtersLastUpd",
      "columns",
      "columnsAdmin",
      "visibleFilters",
      "visibleFiltersAdmin",
      "creditsViewMode"
    ],
    stateReconciler: frozenStateReconciler,
    version: PERSISTED_REDUCER_VERSION,
    migrate: async (state) => {
      if (state?._persist?.version < PERSISTED_REDUCER_VERSION) {
        return {
          ...state,
          ...initialState
        }
      }
      return state
    }
  },
  slice.reducer
)

export const reducer = persistedReducer
